import {
  Box,
  Button,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import React, {useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import ActionButtonGroup from "../../../components/ActionButtonGroup";
import MWSkeleton from "../../../components/MWSkeleton";
import SubTitle from "../../../components/SubTitle";
import {useGetQuery, useRemoveMutation} from "../../../hooks/useQueryHooks";
import {tutorActionButton} from "../../../models/clientModels/data/actionButtonData";
import {ResponseList, ResponseRemove} from "../../../models/clientModels/interfaces/Response";
import {
  EDTutor,
  TutorListParams,
  initialTutorListParams,
} from "../../../models/serverModels/education/EDTutor";
import {FilterGroup, SubMenuGroup} from "../../../styledComponents/StyledListContainer";

import * as tutorApi from "../../../api/educationApi/tutorApi";
import * as Params from "../../../models/clientModels/interfaces/apiParams";

import {useQueryClient} from "react-query";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import {useDepartmentList} from "../../../hooks/useDepartmentList";
import {PaginationContainer} from "../../../styledComponents/StyledPagination";
import {StyledTableContainer, StyledTableHead} from "../../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));

const TutorList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [page, setPage] = useState<number>(1);

  const [filter, setFilter] = useState<TutorListParams>({
    ...initialTutorListParams,
    token: tokenState,
  });
  const queryString = Object.entries(filter)
    .map((e) => e.join("="))
    .join("&");

  const {departmentList} = useDepartmentList();

  const {
    data: tutorList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EDTutor>>({
    key: ["TutorList", {...filter, token: tokenState}],
    api: () => tutorApi.getList({...filter, token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: tutorApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("TutorList");
      },
    },
  });

  const {control, reset, setValue} = useForm<TutorListParams>({
    defaultValues: initialTutorListParams,
  });

  const tutorListFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const resetHandler = () => {
    setFilter(initialTutorListParams);
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="강사목록" />
      <SubMenuGroup>
        <FilterGroup>
          <Box>
            <Controller
              name="filterSearchWord"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="이름"
                  size="small"
                  onChange={tutorListFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="filterCareer"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="경력사항"
                  size="small"
                  onChange={tutorListFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="filterQualification"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="자격현황"
                  size="small"
                  name="creator"
                  onChange={tutorListFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="filterExternalEducation"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="외부교육현황"
                  size="small"
                  name="creator"
                  onChange={tutorListFilteringHandler}
                />
              )}
            />
          </Box>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
        <ActionButtonGroup data={tutorActionButton(queryString)} />
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell rowSpan={2} align="center">
                #
              </TableCell>
              <TableCell rowSpan={2} align="center">
                이름
              </TableCell>
              <TableCell rowSpan={2} align="center">
                부서명
              </TableCell>
              <TableCell rowSpan={2} align="center">
                경력사항
              </TableCell>
              <TableCell rowSpan={2} align="center">
                자격현황
              </TableCell>
              <TableCell rowSpan={2} align="center">
                내부교육이수현황
              </TableCell>
              <TableCell colSpan={2} align="center">
                외부교육이수현황
              </TableCell>
              <TableCell rowSpan={2} align="center">
                비고
              </TableCell>
              <TableCell rowSpan={2} align="center">
                편집
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center">기관명</TableCell>
              <TableCell align="center">교육명</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={10} />
            ) : (
              tutorList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1 ? index + 1 : (page - 1) * initialTutorListParams.rows! + index + 1}
                  </TableCell>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">
                    {departmentList?.items?.map((value) =>
                      value.uid === item.departmentUID ? value?.name : ""
                    )}
                  </TableCell>
                  <TableCell align="center">{item.carreer}</TableCell>
                  <TableCell align="center">{item.qualification}</TableCell>
                  <TableCell align="center">{item.internalEducation}</TableCell>
                  <TableCell align="center">{item.externalEducation1}</TableCell>
                  <TableCell align="center">{item.externalEducation2}</TableCell>
                  <TableCell align="center">{item.note}</TableCell>

                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Education/updateTutor/${item.uid}`, {
                          state: item.uid,
                        });
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 강사를 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={tutorList?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default TutorList;
